import type {
  ConnectionId,
  IsoTimestamp,
} from "@cue-the-night/shared-types";
import {
  isoTimestamp,
} from "@cue-the-night/shared-types";

export const CONNECTED_PROTOCOL_VERSION = 1 as const;

export const DEFAULT_HEARTBEAT_INTERVAL_MS = 15_000;

export interface ClientHelloMessage {
  readonly type: "HELLO";
  readonly protocolVersion: number;
  readonly sentAt: IsoTimestamp;
}

export interface ClientHeartbeatMessage {
  readonly type: "HEARTBEAT";
  readonly protocolVersion: number;
  readonly nonce: string;
  readonly sentAt: IsoTimestamp;
}

export type ClientConnectionMessage =
  | ClientHelloMessage
  | ClientHeartbeatMessage;

export interface ServerWelcomeMessage {
  readonly type: "WELCOME";
  readonly protocolVersion: typeof CONNECTED_PROTOCOL_VERSION;
  readonly connectionId: ConnectionId;
  readonly heartbeatIntervalMs: number;
  readonly serverTime: IsoTimestamp;
}

export interface ServerHeartbeatAckMessage {
  readonly type: "HEARTBEAT_ACK";
  readonly protocolVersion: typeof CONNECTED_PROTOCOL_VERSION;
  readonly nonce: string;
  readonly serverTime: IsoTimestamp;
}

export type ProtocolErrorCode =
  | "INVALID_JSON"
  | "INVALID_MESSAGE"
  | "UNSUPPORTED_PROTOCOL";

export interface ServerProtocolErrorMessage {
  readonly type: "PROTOCOL_ERROR";
  readonly protocolVersion: typeof CONNECTED_PROTOCOL_VERSION;
  readonly code: ProtocolErrorCode;
  readonly message: string;
  readonly serverTime: IsoTimestamp;
}

export type ServerConnectionMessage =
  | ServerWelcomeMessage
  | ServerHeartbeatAckMessage
  | ServerProtocolErrorMessage;

export type DecodeClientConnectionMessageResult =
  | {
      readonly status: "OK";
      readonly message: ClientConnectionMessage;
    }
  | {
      readonly status: "ERROR";
      readonly code: ProtocolErrorCode;
    };

function isRecord(
  value: unknown,
): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  );
}

function parseTimestamp(
  value: unknown,
): IsoTimestamp | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  try {
    return isoTimestamp(value);
  } catch {
    return undefined;
  }
}

function isProtocolVersion(
  value: unknown,
): value is number {
  return Number.isInteger(value);
}

export function decodeClientConnectionMessage(
  payload: string,
): DecodeClientConnectionMessageResult {
  let parsed: unknown;

  try {
    parsed = JSON.parse(payload) as unknown;
  } catch {
    return {
      status: "ERROR",
      code: "INVALID_JSON",
    };
  }

  if (!isRecord(parsed)) {
    return {
      status: "ERROR",
      code: "INVALID_MESSAGE",
    };
  }

  if (!isProtocolVersion(parsed.protocolVersion)) {
    return {
      status: "ERROR",
      code: "INVALID_MESSAGE",
    };
  }

  if (
    parsed.protocolVersion !==
    CONNECTED_PROTOCOL_VERSION
  ) {
    return {
      status: "ERROR",
      code: "UNSUPPORTED_PROTOCOL",
    };
  }

  const sentAt = parseTimestamp(parsed.sentAt);

  if (sentAt === undefined) {
    return {
      status: "ERROR",
      code: "INVALID_MESSAGE",
    };
  }

  if (parsed.type === "HELLO") {
    return {
      status: "OK",
      message: {
        type: "HELLO",
        protocolVersion:
          CONNECTED_PROTOCOL_VERSION,
        sentAt,
      },
    };
  }

  if (
    parsed.type === "HEARTBEAT" &&
    typeof parsed.nonce === "string" &&
    parsed.nonce.length > 0
  ) {
    return {
      status: "OK",
      message: {
        type: "HEARTBEAT",
        protocolVersion:
          CONNECTED_PROTOCOL_VERSION,
        nonce: parsed.nonce,
        sentAt,
      },
    };
  }

  return {
    status: "ERROR",
    code: "INVALID_MESSAGE",
  };
}
